import {
  HumiditySource,
  ReferenceHumidity,
  HumidityCurve,
} from 'sigma-mappings';
import mappingFields from '@/domain/data/installation/engineering/isolationAging/mappingFields';
import {
  getStartOfDay,
  minus,
} from '@/services/dateTime/datetimeService';

const parametersFields = parameters => ({
  fixedPeriod: {
    label: mappingFields.FixedPeriod,
    required: true,
    default: false,
    disabled: false,
    hidden: false,
  },
  initialDate: {
    label: mappingFields.InitialDate,
    required: parameters.fixedPeriod,
    maxLength: 10,
    default: minus(getStartOfDay(), { days: 90 }),
    disabled: !parameters.fixedPeriod,
    hidden: false,
    isDateValid: () => (!parameters.fixedPeriod || (!parameters.isDateRelationInvalid
      || (parameters.isDateRelationInvalid && parameters.isSameDate))),
    isMinDateValid: () => (!parameters.fixedPeriod || !parameters.isInitialMinDateInvalid),
    isDateIntervalValid: () => (!parameters.fixedPeriod || !parameters.isDateIntervalInvalid),
  },
  finalDate: {
    label: mappingFields.FinalDate,
    required: parameters.fixedPeriod,
    maxLength: 10,
    default: getStartOfDay(),
    disabled: !parameters.fixedPeriod,
    hidden: false,
    isDateValid: () => (!parameters.fixedPeriod || (!parameters.isDateRelationInvalid
      || (parameters.isDateRelationInvalid && parameters.isSameDate))),
    isMinDateValid: () => (!parameters.fixedPeriod || !parameters.isFinalMinDateInvalid),
    isDateIntervalValid: () => (!parameters.fixedPeriod || !parameters.isDateIntervalInvalid),
  },
  initialTime: {
    label: mappingFields.InitialTime,
    required: parameters.fixedPeriod,
    maxLength: 8,
    default: '00:00:00',
    disabled: !parameters.fixedPeriod,
    hidden: false,
    isDateValid: () => (!parameters.fixedPeriod || (!parameters.isDateRelationInvalid
      || (parameters.isDateRelationInvalid && !parameters.isSameDate))),
    isDateIntervalValid: () => (!parameters.fixedPeriod || !parameters.isDateIntervalInvalid),
  },
  finalTime: {
    label: mappingFields.FinalTime,
    required: parameters.fixedPeriod,
    maxLength: 8,
    default: '23:59:59',
    disabled: !parameters.fixedPeriod,
    hidden: false,
    isDateValid: () => (!parameters.fixedPeriod || (!parameters.isDateRelationInvalid
      || (parameters.isDateRelationInvalid && !parameters.isSameDate))),
    isDateIntervalValid: () => (!parameters.fixedPeriod || !parameters.isDateIntervalInvalid),
  },
  days: {
    label: mappingFields.Days,
    required: true,
    maxLength: 6,
    precision: 0,
    default: 30,
    minValue: 1,
    maxValue: 20000,
    disabled: false,
    hidden: false,
  },
  considerHumidity: {
    label: mappingFields.ConsiderHumidity,
    required: true,
    default: false,
    disabled: false,
    hidden: false,
  },
  paperHumiditySource: {
    label: mappingFields.PaperHumiditySource,
    required: parameters.considerHumidity,
    default: null,
    disabled: !parameters.considerHumidity,
    hidden: false,
  },
  paperHumidity: {
    label: mappingFields.PaperHumidity,
    required: parameters.considerHumidity
      && parameters.paperHumiditySource !== HumiditySource.WATER_ON_PAPER,
    maxLength: 4,
    precision: 1,
    default: null,
    minValue: 0.1,
    maxValue: 99.9,
    disabled: !parameters.considerHumidity,
    hidden: !parameters.considerHumidity
      || parameters.paperHumiditySource === HumiditySource.WATER_ON_PAPER,
  },
  referenceHumidity: {
    label: mappingFields.ReferenceHumidity,
    required: parameters.considerHumidity,
    default: null,
    disabled: !parameters.considerHumidity,
    hidden: false,
  },
  referenceHumidityPercentage: {
    label: mappingFields.ReferenceHumidityPercentage,
    required: parameters.considerHumidity
      && parameters.referenceHumidity === ReferenceHumidity.CUSTOM,
    maxLength: 4,
    precision: 1,
    default: null,
    minValue: 0.1,
    maxValue: 99.9,
    disabled: false,
    hidden: !parameters.considerHumidity
      || parameters.referenceHumidity !== ReferenceHumidity.CUSTOM,
  },
  humidityCurveMethod: {
    label: mappingFields.HumidityCurveMethod,
    required: parameters.considerHumidity,
    default: null,
    disabled: !parameters.considerHumidity,
    hidden: false,
  },
  oxygenOil: {
    label: mappingFields.OxygenOil,
    required: parameters.considerHumidity
      && parameters.humidityCurveMethod === HumidityCurve.OXYGEN_DAN_MARTIN,
    maxLength: 11,
    precision: 1,
    default: null,
    minValue: 0.1,
    maxValue: 1000000.0,
    disabled: false,
    hidden: !parameters.considerHumidity
      || parameters.humidityCurveMethod !== HumidityCurve.OXYGEN_DAN_MARTIN,
  },
  totalAssetLifeExpectancyInYears: {
    label: mappingFields.TotalAssetLifeExpectancyInYears,
    required: true,
    maxLength: 3,
    precision: 0,
    default: 35,
    minValue: 1,
    maxValue: 100,
    disabled: false,
    hidden: false,
  },
});

export default parametersFields;
